// Capstone board API (ADO #510).
//
//   GET  /api/capstone/{slug}             the board: stories grouped by feature,
//                                         state derived from the caller's attempts
//   GET  /api/capstones/started           capstones with a project — dashboard card
//   POST /api/learn/practice/{slug}/attempt   grade ONE story (the ordinary path)
//
// A story is always graded by naming its own slug. The project all stories
// share records only the story that created it, so nothing here reads "which
// story" off the project.
import { authFetch } from './projectService';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

async function jsonOrThrow(res) {
  if (res.ok) return res.json();
  const body = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
  const err = new Error(body.detail || `HTTP ${res.status}`);
  err.status = res.status;
  throw err;
}

export async function getCapstoneBoard(slug) {
  return jsonOrThrow(await authFetch(`${API_URL}/api/capstone/${encodeURIComponent(slug)}`));
}

export async function getStartedCapstones() {
  return jsonOrThrow(await authFetch(`${API_URL}/api/capstones/started`));
}

// One story's acceptance tests, run server-side against ITS test_cases.
export async function submitStory(storySlug, { studentId, filesDict, mainFile, includedFiles }) {
  const res = await authFetch(
    `${API_URL}/api/learn/practice/${encodeURIComponent(storySlug)}/attempt`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ student_id: studentId, files: filesDict, main_file: mainFile,
        included_files: includedFiles }),
    },
  );
  return jsonOrThrow(res);
}

// ADO #514 — the caller's build record across the whole capstone. Every figure
// is one the server observed; nothing a client claimed is summed.
export async function getCapstoneRecord(slug) {
  return jsonOrThrow(await authFetch(`${API_URL}/api/capstone/${encodeURIComponent(slug)}/record`));
}

// ADO #513 — the student's read-only share link for a capstone.
export async function getShare(slug) {
  return jsonOrThrow(await authFetch(`${API_URL}/api/capstone/${encodeURIComponent(slug)}/share`));
}

export async function createShare(slug) {
  return jsonOrThrow(await authFetch(
    `${API_URL}/api/capstone/${encodeURIComponent(slug)}/share`, { method: 'POST' }));
}

export async function revokeShare(slug) {
  return jsonOrThrow(await authFetch(
    `${API_URL}/api/capstone/${encodeURIComponent(slug)}/share`, { method: 'DELETE' }));
}

// What a share link shows. NO auth: the visitor is not signed in, and the token
// in the URL is the whole credential.
export async function getSharedCapstone(token) {
  return jsonOrThrow(await fetch(`${API_URL}/api/shared/capstone/${encodeURIComponent(token)}`));
}

export const shareUrl = (slug, token) =>
  `${window.location.origin}/capstone/${encodeURIComponent(slug)}/s/${encodeURIComponent(token)}`;

// The course's PUBLISHED capstones (migration 385), with the caller's progress
// when signed in. One list for every doorway: the Learn sidebar's Module
// dropdown, the Course outline, the dashboard.
export async function listCapstones(courseSlug) {
  const q = courseSlug ? `?course_slug=${encodeURIComponent(courseSlug)}` : '';
  return jsonOrThrow(await authFetch(`${API_URL}/api/capstones${q}`));
}

// Migration 395 — the caller's own copy of the capstone's FINISHED engine, made
// on first use. Opened in the SQL client only: it is for using, not building.
export async function openFinishedEngine(slug) {
  return jsonOrThrow(await authFetch(
    `${API_URL}/api/capstone/${encodeURIComponent(slug)}/finished`, { method: 'POST' }));
}
